import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import ArcheLogo from './ArcheLogo';

const navLinks = [
  { name: 'About', href: '#about' },
  { name: 'Services', href: '#services' },
  { name: 'Technologies', href: '#technologies' },
  { name: 'Industries', href: '#industries' },
  { name: 'Process', href: '#process' },
  { name: 'Contact', href: '#contact' }
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-[#050505]/85 backdrop-blur-2xl border-b border-white/10'
            : 'bg-transparent'
        }`}
      >
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-32 md:h-36">
            {/* Logo (integrated wordmark) */}
            <a href="#" className="flex items-center group" data-testid="header-logo">
              <div className="transition-transform duration-300 group-hover:scale-105">
                <ArcheLogo variant="light" size="sm" />
              </div>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className="text-sm font-medium text-gray-400 hover:text-[#6B8FCC] transition-colors duration-300 relative group"
                >
                  {link.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-[#1E3A6F] to-[#4A6FA5] group-hover:w-full transition-all duration-300" />
                </a>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Button
                data-testid="header-get-quote-btn"
                className="px-6 py-2 bg-gradient-to-r from-[#1E3A6F] to-[#4A6FA5] hover:from-[#2A4A85] hover:to-[#5B80B8] text-white border-0 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-blue-900/50"
              >
                Get Quote
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-white hover:text-[#6B8FCC] transition-colors"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3 }}
            className="fixed top-32 md:top-36 right-0 bottom-0 w-full max-w-sm bg-[#050505]/95 backdrop-blur-2xl border-l border-white/10 z-40 lg:hidden"
          >
            <nav className="flex flex-col gap-6 p-8">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-xl font-semibold text-white hover:text-[#6B8FCC] transition-colors"
                >
                  {link.name}
                </a>
              ))}
              <Button
                data-testid="mobile-get-quote-btn"
                className="mt-4 px-6 py-3 bg-gradient-to-r from-[#1E3A6F] to-[#4A6FA5] hover:from-[#2A4A85] hover:to-[#5B80B8] text-white border-0 rounded-lg"
              >
                Get Quote
              </Button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
